// The Create wizard's one Figma line.
//
// The wizard files the GitLab issue BEFORE any session sees the text. The first
// place the server can scan a pasted link is the refine seed that follows (the
// `user_message` branch in `ws/stream.ts`, scanning the seed's `evidenceText` —
// the bytes the user typed). That is the right place for the chip and for the
// model's pointer, and the wrong place to find out Figma was never connected:
// by then the issue exists and the user is answering questions. So this line
// says it on the step where the link was typed, with the way to fix it.
//
// Recognition is CLIENT-SIDE and reads nothing: a pattern over the text in the
// box. The one request is Loom's own `GET /api/figma/status` — never Figma —
// made only once a readable link appears. Anything that read cannot say truly
// renders nothing: a 404 (Figma is off in this environment), a failed read, a
// credential that already exists.
//
// A Connect started here is a full-page round trip that comes back to the same
// URL. The stage and the draft are restored from sessionStorage (FocusPage), so
// the user lands on this step with their text, and the `?figma=` marker is read
// once and stripped, as `FigmaConnection` and `FigmaLinkPanel` do.
//
// No regex lookbehind anywhere here: this ships to safari14.

import { useEffect, useState } from "react";
import { z } from "zod";

import { useT } from "../../i18n/index.js";
import { httpGet } from "../../lib/http";

import {
  FIGMA_RESULT_PARAM,
  figmaConnectHref,
  figmaResultCopyKey,
  figmaReturnToPath,
} from "./figmaCopy";
import { useHereLocation } from "./useHereLocation";

/**
 * A Figma link Loom could READ once connected: a design, a legacy file, a
 * FigJam board or a prototype, with a file key. Slides, decks and Community
 * files are left out — connecting would not make those readable, so offering
 * Connect for them would promise something that cannot happen.
 */
const READABLE_FIGMA_LINK_RE =
  /https?:\/\/(?:[a-z0-9-]+\.)*figma\.com\/(?:file|design|proto|board)\/[a-z0-9]{10,}/i;

export const hasReadableFigmaLink = (text: string): boolean =>
  READABLE_FIGMA_LINK_RE.test(text);

const FigmaStatusSchema = z.object({ connected: z.boolean() });

type Status = "unknown" | "hidden" | "not_connected" | "connected";

export function CreateFigmaHint({
  text,
  location,
}: {
  /** The wizard's task text, as typed. */
  readonly text: string;
  /** Test seam, as on `FigmaConnection`. A handed-in location is never written back. */
  readonly location?: { readonly pathname: string; readonly search: string };
}) {
  const t = useT();
  const live = useHereLocation();
  const here = location ?? live.here;
  const hasLink = hasReadableFigmaLink(text);
  const [status, setStatus] = useState<Status>("unknown");
  const [resultKey, setResultKey] = useState<string | null>(() =>
    figmaResultCopyKey(new URLSearchParams(here.search).get(FIGMA_RESULT_PARAM)),
  );

  const replaceUrl = live.replace;
  const overridden = location !== undefined;
  const hash = overridden ? "" : live.here.hash;
  useEffect(() => {
    const params = new URLSearchParams(here.search);
    if (!params.has(FIGMA_RESULT_PARAM)) return;
    setResultKey(figmaResultCopyKey(params.get(FIGMA_RESULT_PARAM)));
    if (overridden) return;
    replaceUrl(`${figmaReturnToPath(here.pathname, here.search)}${hash}`);
  }, [here.pathname, here.search, hash, overridden, replaceUrl]);

  // One status read, and only once there is a link to talk about. Typing more
  // text around the link does not read again.
  useEffect(() => {
    if (!hasLink || status !== "unknown") return;
    let alive = true;
    void (async () => {
      let next: Status;
      try {
        const res = await httpGet("/api/figma/status", FigmaStatusSchema);
        next = res.connected ? "connected" : "not_connected";
      } catch {
        next = "hidden";
      }
      if (alive) setStatus(next);
    })();
    return () => {
      alive = false;
    };
  }, [hasLink, status]);

  if (!hasLink || status !== "not_connected") return null;
  const href = figmaConnectHref(figmaReturnToPath(here.pathname, here.search));
  return (
    <p className="pk-advisory fig-create-hint">
      {resultKey !== null && <>{t(resultKey)} </>}
      {t("focus.figma.create.linkNeedsConnect")}{" "}
      <a className="focus-link" href={href}>
        {t("focus.figma.connect")}
      </a>
    </p>
  );
}
